import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  HandCoins,
  LayoutDashboard,
  ListTodo,
  LogOut,
  MessageSquare,
  Moon,
  Settings,
  Sun,
  UserCheck,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context-value';
import { useNotificationsRealtime } from '../lib/notifications';
import { apiAssetUrl, notificationsApi } from '../lib/resources';
import { setUiPrefs, useUiPrefs } from '../lib/ui-prefs';
import { AvailabilitySwitch } from './AvailabilitySwitch';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

/**
 * Barra lateral do escopo definido na spec.
 * Fora do escopo por decisão do dono: Impulsos e Agentes de IA.
 */
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Início', icon: LayoutDashboard, end: true },
  { to: '/pipelines', label: 'Pipelines', icon: Filter },
  { to: '/leads', label: 'Leads', icon: Users },
  { to: '/atividades', label: 'Atividades', icon: ListTodo },
  { to: '/automacoes', label: 'Automações', icon: Workflow },
  { to: '/chat', label: 'Chat ao vivo', icon: MessageSquare },
  { to: '/calendario', label: 'Calendário', icon: CalendarDays },
  { to: '/notificacoes', label: 'Notificações', icon: Bell },
  { to: '/configuracoes', label: 'Configurações', icon: Settings },
];

export function AppShell() {
  const { session, logout, can } = useAuth();
  const logo = apiAssetUrl(session?.workspace.logoUrl);
  // Vendas tem tela própria (de celular), fora deste menu: aqui fica só o atalho.
  const navItems: NavItem[] = [
    ...NAV_ITEMS.slice(0, 3),
    ...(can('sales_admin', 'sales_member')
      ? [{ to: '/vendas', label: 'Vendas', icon: HandCoins }]
      : []),
    ...NAV_ITEMS.slice(3, 8),
    // Disponibilidade da equipe, ausências e relatório da distribuição: gestor.
    ...(can('pipeline_admin') ? [{ to: '/equipe', label: 'Equipe', icon: UserCheck }] : []),
    ...NAV_ITEMS.slice(8),
  ];
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toasts, dismiss } = useNotificationsRealtime();
  // Contador do sino: o socket invalida na hora; a consulta periódica é a reserva.
  const unread = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
  }).data?.unread;
  const read = useMutation({
    mutationFn: notificationsApi.read,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const { navCollapsed: collapsed, theme } = useUiPrefs();
  const toggleTheme = () => setUiPrefs({ theme: theme === 'dark' ? 'light' : 'dark' });
  const ThemeIcon = theme === 'dark' ? Sun : Moon;
  const themeLabel = theme === 'dark' ? 'Modo claro' : 'Modo escuro';
  const badge = Boolean(unread) && (
    <span
      className="rounded-full px-1.5 text-[10px] font-semibold leading-4"
      style={{ backgroundColor: 'var(--crm-accent)', color: '#0f172a' }}
      aria-label={`${unread} não lidas`}
    >
      {unread! > 99 ? '99+' : unread}
    </span>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      <aside
        className={`flex shrink-0 flex-col transition-[width] ${collapsed ? 'w-14' : 'w-56'}`}
        style={{
          backgroundColor: 'var(--crm-sidebar)',
          color: 'var(--crm-sidebar-foreground)',
        }}
      >
        <div
          className={`relative ${collapsed ? 'flex flex-col items-center px-2 py-4' : 'px-3 py-4'}`}
        >
          <button
            type="button"
            onClick={() => setUiPrefs({ navCollapsed: !collapsed })}
            className={`rounded p-0.5 opacity-60 hover:opacity-100 ${collapsed ? 'mb-3' : 'absolute right-2 top-2'}`}
            title={collapsed ? 'Abrir o menu' : 'Recolher o menu'}
            aria-label={collapsed ? 'Abrir o menu' : 'Recolher o menu'}
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
          {logo && (
            <img
              src={logo}
              alt={session?.workspace.name}
              className={
                collapsed ? 'h-7 w-7 object-contain' : 'mb-3 h-8 max-w-full object-contain'
              }
            />
          )}
          {!collapsed && <WorkspaceSwitcher />}
        </div>

        <nav
          className={`min-h-0 flex-1 space-y-0.5 overflow-y-auto ${collapsed ? 'px-2' : 'px-2'}`}
        >
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                `relative flex items-center rounded-lg text-[13px] transition ${
                  collapsed ? 'justify-center px-0 py-2' : 'gap-2.5 px-2.5 py-1.5'
                } ${isActive ? 'font-semibold' : 'opacity-75 hover:opacity-100'}`
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      backgroundColor: 'var(--crm-primary)',
                      color: 'var(--crm-primary-foreground)',
                    }
                  : undefined
              }
            >
              <item.icon size={17} strokeWidth={1.9} className="shrink-0" />
              {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
              {item.to === '/notificacoes' &&
                badge &&
                (collapsed ? (
                  <span className="absolute -right-0.5 -top-0.5">{badge}</span>
                ) : (
                  badge
                ))}
            </NavLink>
          ))}
        </nav>

        {collapsed ? (
          <div className="flex flex-col items-center gap-3 border-t border-white/10 py-3">
            <AvailabilitySwitch compact />
            <button
              type="button"
              onClick={toggleTheme}
              className="opacity-70 hover:opacity-100"
              title={themeLabel}
              aria-label={themeLabel}
            >
              <ThemeIcon size={16} />
            </button>
            <button
              type="button"
              onClick={logout}
              className="opacity-70 hover:opacity-100"
              title="Sair"
              aria-label="Sair"
            >
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="border-t border-white/10 px-3 py-3 text-[13px]">
            <div className="mb-3 border-b border-white/10 pb-3">
              <AvailabilitySwitch />
            </div>
            <p className="truncate font-medium">{session?.user.name}</p>
            <p className="truncate text-xs opacity-60">{session?.user.email}</p>
            <div className="mt-2 flex items-center justify-between">
              <button
                type="button"
                onClick={logout}
                className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
              >
                <LogOut size={13} /> Sair
              </button>
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
                title={`Mudar para o ${themeLabel.toLowerCase()}`}
              >
                <ThemeIcon size={13} /> {themeLabel}
              </button>
            </div>
          </div>
        )}
      </aside>

      <main className="h-full min-w-0 flex-1 overflow-auto bg-slate-50">
        <Outlet />
      </main>

      {/* Pop-ups das notificações (Fase 6) */}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((notice) => (
          <div
            key={notice.id}
            className="pointer-events-auto rounded-xl border border-slate-200 bg-white p-3 text-sm shadow-lg"
          >
            <div className="flex items-start gap-2">
              <button
                type="button"
                className="min-w-0 flex-1 text-left"
                onClick={() => {
                  dismiss(notice.id);
                  read.mutate(notice.id);
                  if (notice.link) navigate(notice.link);
                }}
              >
                <span className="block font-medium text-slate-900">{notice.title}</span>
                {notice.body && (
                  <span className="block truncate text-xs text-slate-500">{notice.body}</span>
                )}
              </button>
              <button
                type="button"
                className="text-xs text-slate-400 hover:text-slate-700"
                aria-label="Fechar"
                onClick={() => dismiss(notice.id)}
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
